import { call, put, select } from 'redux-saga/effects';
import { refreshReceiptStatisticsSaga } from './refreshReceiptStatisticsSaga';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { logoutRequest } from 'store/auth/actions';
import { checkResponse } from 'store/utils';
import { renderErrorMessage } from 'utils/render-error-message';
import { getProviderName } from 'utils/get-provider-name';
import { Organisation } from 'store/organisation/models';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { uploadReceiptToProvider } from '../api';
import { MessageStates } from 'containers/MessageBox';
import {
  fetchAllExcelTransactionsRequest,
  fetchExcelFilesMetadataRequest,
  updateReceiptFileStatus,
  uploadReceiptToProviderSuccess,
  uploadReceiptToProviderFailure,
} from '../actions';
import i18n from 'i18next';

export function* uploadReceiptToProviderSaga(action: any) {
  const requestedFilesIds: string[] = Array.isArray(action?.payload?.filesIds)
    ? action.payload.filesIds.filter(Boolean)
    : [];
  try {
    const { connectionId, filesIds, bankAccountId, status } = action.payload;

    const response: any = yield call(
      uploadReceiptToProvider,
      connectionId,
      filesIds,
      status,
      bankAccountId
    );

    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const uploadResults = Array.isArray(response?.data)
      ? response.data
      : response?.data?.data
      ? [response.data]
      : [];
    const organisations: Organisation[] = yield select(getOrgOrganisations);
    const currentOrganisation = organisations?.find(
      (org) => org?.uuid === connectionId || org?.connectionId === connectionId
    );
    const fileNamesById = new Map(
      (currentOrganisation?.excelFilesMetadata ?? []).map((file) => [
        file.excelMetadataId,
        file.fileName,
      ])
    );

    const providerName = getProviderName(currentOrganisation?.providerName);

    let hasSuccessfulUploads = false;

    for (const uploadResult of uploadResults) {
      const data = uploadResult?.data ?? uploadResult;
      const fileId =
        uploadResult?.fileId ??
        data?.fileId ??
        data?.excelMetadataId ??
        data?.importId;
      const fileName = fileId ? fileNamesById.get(fileId) : null;
      const fileLabel = fileName ? `File "${fileName}"` : `File`;
      let message: MessageData;

      if (data?.success) {
        message = {
          title: i18n.t('common.success-exclamation'),
          description: i18n.t('organisation.file-post-success-description', {
            fileLabel,
            provider: providerName,
          }),
          type: MessageStates.SUCCESS,
        };
        hasSuccessfulUploads = true;
      } else if (data?.validationErrors) {
        const validationErrors = Array.isArray(data.validationErrors)
          ? data.validationErrors
          : [data.validationErrors];
        const result = validationErrors.map((item) => `- ${item}`).join('\n');
        message = {
          title: i18n.t('notifications.validation-errors-title'),
          description: i18n.t(
            'notifications.validation-errors-file-description',
            { fileLabel, result }
          ),
          type: MessageStates.WARNING,
        };
      } else if (data?.errors) {
        const postErrors = Array.isArray(data.errors) ? data.errors : [data.errors];
        const result = postErrors.map((item) => `- ${item}`).join('\n');
        message = {
          title: i18n.t('notifications.post-errors-title'),
          description: i18n.t('notifications.post-errors-file-description', {
            fileLabel,
            result,
          }),
          type: MessageStates.ERROR,
        };
      } else {
        message = {
          title: i18n.t('notifications.upload-completed-title'),
          description: i18n.t('notifications.upload-completed-description', {
            fileLabel,
          }),
          type: MessageStates.INFO,
        };
      }

      yield put(turnMessageOn(message));

      if (data?.success && fileId) {
        yield put(
          updateReceiptFileStatus(
            connectionId,
            String(fileId),
            String(data?.link ?? '')
          )
        );
      }
    }

    if (hasSuccessfulUploads) {
      // Keep right-side file metadata and table in sync after posting.
      yield put(fetchExcelFilesMetadataRequest(connectionId));
      yield put(fetchAllExcelTransactionsRequest(connectionId, 1, 10));
    }

    yield put(uploadReceiptToProviderSuccess(requestedFilesIds));
    if (hasSuccessfulUploads) {
      yield call(refreshReceiptStatisticsSaga);
    }
  } catch (error: any) {
    yield put(uploadReceiptToProviderFailure(requestedFilesIds));
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}
